import { useState } from 'react';
import { ChevronLeft, ChevronRight, FileText, FileSpreadsheet, Bell, RefreshCw, AlertTriangle, Info, Banknote, TrendingUp, Activity, Landmark, Scale, Earth } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { KpiCard } from '@/components/ui/KpiCard';
import { WelcomeBanner } from '@/components/finance/WelcomeBanner';
import { RevenueChart } from '@/components/finance/RevenueChart';
import { ChargesBreakdown } from '@/components/finance/ChargesBreakdown';
import { useFinanceKpis, useFinanceKpisHistory } from '@/hooks/useFinanceKpis';
import { useAuthStore } from '@/stores/authStore';
import { formatMontant } from '@/utils/currency';
import { exportToXLSX, exportToPDF, buildExportFilename, type ExportColumn } from '@/utils/exports';
import type { FinanceKpis } from '@/api/finance';
import { formatPeriode } from '@/utils/date';

/**
 * Dashboard Finance pixel-perfect CTO-FRONT-002.
 * Référence : docs/briefs/CTO-FRONT-002/design-dashboard-finance-v1.html
 */

const MONTHS_FR = ['janv.','févr.','mars','avr.','mai','juin','juil.','août','sept.','oct.','nov.','déc.'];

const fmtPeriodLabel = (periode: string) => {
  const [year, month] = periode.split('-').map(Number);
  const m = MONTHS_FR[month - 1] ?? '';
  return `${m.charAt(0).toUpperCase() + m.slice(1)} ${year}`;
};

const shiftPeriode = (periode: string, delta: number): string => {
  const [year, month] = periode.split('-').map(Number);
  const d = new Date(Date.UTC(year, month - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

export const DashboardPage = () => {
  const filiale  = useAuthStore((s) => s.filiale);
  const user     = useAuthStore((s) => s.user);
  const [periode, setPeriode] = useState(formatPeriode());

  const { data: kpis,    isLoading: kpisLoading,  refetch: refetchKpis } = useFinanceKpis(periode);
  const { data: history, isLoading: historyLoading, refetch: refetchHist } = useFinanceKpisHistory(12);

  const handleRefresh = () => { void refetchKpis(); void refetchHist(); };

  const firstName  = user?.fullName.split(' ')[0];
  const year       = periode.split('-')[0];

  // Ratios depuis kpis.metrics (calculé serveur)
  const tauxMargeBrute = kpis?.metrics?.tauxMargeBrute ?? 0;
  const tauxEbitda     = kpis?.metrics?.tauxEbitda     ?? 0;
  const tauxCharges    = 1 - tauxMargeBrute;

  // Une période sans aucune écriture affiche des zéros partout. Sans explication,
  // un DAF qui a facturé lit « CA : 0 » et en conclut que l'outil est cassé.
  // On distingue donc « zéro mesuré » de « rien à mesurer », et on renvoie vers
  // la dernière période réellement mouvementée.
  // ── Exports Excel / PDF ────────────────────────────────────
  // Les deux boutons de la barre d'outils existaient depuis l'origine mais sans
  // onClick : ils ne faisaient rien. On exporte la serie historique plutot
  // qu'une photo des cinq cartes — c'est ce qui est exploitable dans un rapport.
  const enFrancs = (cents: string) => Number(BigInt(cents)) / 100;

  const colonnesExport: ExportColumn<FinanceKpis>[] = [
    { header: 'Période',            accessor: (k) => fmtPeriodLabel(k.periode) },
    { header: "Chiffre d'affaires", accessor: (k) => enFrancs(k.caCents),         align: 'right' },
    { header: 'Charges',            accessor: (k) => enFrancs(k.chargesCents),    align: 'right' },
    { header: 'Marge brute',        accessor: (k) => enFrancs(k.margeBruteCents), align: 'right' },
    { header: 'EBITDA',             accessor: (k) => enFrancs(k.ebitdaCents),     align: 'right' },
    { header: 'Trésorerie nette',   accessor: (k) => enFrancs(k.tresorerieCents), align: 'right' },
    { header: 'BFR',                accessor: (k) => enFrancs(k.bfrCents),        align: 'right' },
    { header: 'DSO (jours)',        accessor: (k) => k.dsoDays,                   align: 'right' },
  ];

  const lignesExport   = history?.series ?? [];
  const filialeKey     = filiale?.filialeKey;
  const exportPossible = lignesExport.length > 0;

  const onExportXLSX = () => exportToXLSX({
    filename:  buildExportFilename('Situation-financiere', filialeKey, 'xlsx'),
    sheetName: 'KPI mensuels',
    columns:   colonnesExport,
    rows:      lignesExport,
  });

  const onExportPDF = () => exportToPDF({
    title:       `Situation financière — ${filiale?.fullName ?? ''}`,
    subtitle:    `SYSCOHADA révisé · ${lignesExport.length} mois · montants en ${kpis?.currency ?? 'FCFA'}`,
    filename:    buildExportFilename('Situation-financiere', filialeKey, 'pdf'),
    orientation: 'landscape',
    columns:     colonnesExport,
    rows:        lignesExport,
  });

  // Cumul du CA depuis janvier jusqu'au mois affiché. Un CA mensuel à zéro ne
  // dit rien de l'année : en septembre le mois est vide alors que l'exercice a
  // bien produit. Le cumul remet le mois dans sa perspective annuelle.
  // LOT A / A-2 : le cumul n'est plus calculé ici. L'écran additionnait les
  // snapshots mensuels reçus, avec sa propre idée de ce qu'est l'exercice.
  // Le serveur le calcule et l'envoie avec les KPIs de la période.
  const anneeAffichee = periode.slice(0, 4);
  const cumulAnnuel = kpis?.cumulAnnuelCaCents ? BigInt(kpis.cumulAnnuelCaCents) : 0n;

  const periodeSansEcriture =
    !!kpis && BigInt(kpis.caCents) === 0n && BigInt(kpis.chargesCents) === 0n;
  const dernierePeriodeActive = history?.series
    ?.filter((s) => s.periode !== periode && (BigInt(s.caCents) !== 0n || BigInt(s.chargesCents) !== 0n))
    .sort((a, b) => b.periode.localeCompare(a.periode))[0];

  // Décomposition charges (en attente d'un endpoint dédié — répartition type secteur BTP)
  const chargesTotalCents = kpis ? Number(BigInt(kpis.chargesCents)) : 0;
  const charges = chargesTotalCents > 0
    ? [
        { label: 'Achats matières (601)',       amount: Math.round(chargesTotalCents * 0.48), fill: 'f1' as const },
        { label: 'Salaires & charges (641)',    amount: Math.round(chargesTotalCents * 0.24), fill: 'f2' as const },
        { label: 'Transport & carburant (621)', amount: Math.round(chargesTotalCents * 0.12), fill: 'f3' as const },
        { label: 'Autres charges',              amount: Math.round(chargesTotalCents * 0.16), fill: 'f4' as const },
      ]
    : [];

  // Trend KPI : pour MVP, on extrait depuis history (mois courant vs précédent)
  const computeTrend = (
    getter: (s: typeof kpis) => bigint | number | string | null | undefined,
  ): { label: string; direction: 'up' | 'down' | 'flat' } | undefined => {
    if (!history || history.series.length < 2) return undefined;
    const curr = history.series[history.series.length - 1];
    const prev = history.series[history.series.length - 2];
    const c = Number(getter(curr) ?? 0);
    const p = Number(getter(prev) ?? 0);
    if (p === 0) return c > 0 ? { label: 'Démarrage', direction: 'up' } : undefined;
    const pct = ((c - p) / Math.abs(p)) * 100;
    if (Math.abs(pct) < 1) return { label: 'Stable', direction: 'flat' };
    return { label: `${pct > 0 ? '+' : ''}${pct.toFixed(0)}% vs mois préc.`, direction: pct > 0 ? 'up' : 'down' };
  };

  return (
    <>
      <Topbar
        title="Dashboard Finance"
        subtitle={filiale ? `${filiale.fullName} · Exercice ${year}` : 'Vue d\'ensemble'}
        actions={
          <>
            <div className="ds-period-nav">
              <button onClick={() => setPeriode(shiftPeriode(periode, -1))} aria-label="Mois précédent">
                <ChevronLeft size={14} />
              </button>
              <span className="ds-period-label">{fmtPeriodLabel(periode)}</span>
              <button onClick={() => setPeriode(shiftPeriode(periode, +1))} aria-label="Mois suivant">
                <ChevronRight size={14} />
              </button>
            </div>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={handleRefresh} aria-label="Rafraîchir">
              <RefreshCw size={14} className={kpisLoading ? 'animate-spin' : ''} />
            </button>
            <button
              className="ds-btn ds-btn-g ds-btn-sm"
              onClick={onExportPDF}
              disabled={!exportPossible}
              title={exportPossible ? 'Exporter la situation en PDF' : 'Aucune donnée à exporter'}
            >
              <FileText size={14} /> PDF
            </button>
            <button
              className="ds-btn ds-btn-g ds-btn-sm"
              onClick={() => void onExportXLSX()}
              disabled={!exportPossible}
              title={exportPossible ? 'Exporter la situation en Excel' : 'Aucune donnée à exporter'}
            >
              <FileSpreadsheet size={14} /> Excel
            </button>
            <button
              className="ds-btn ds-btn-g"
              style={{ width: 36, height: 36, padding: 0, justifyContent: 'center', position: 'relative' }}
              aria-label="Notifications"
            >
              <Bell size={16} />
            </button>
          </>
        }
      />

      <div className="ds-content">
        {/* Welcome banner */}
        <WelcomeBanner
          firstName={firstName}
          subtitle={
            filiale
              ? `Voici la situation financière de ${filiale.name} ce mois-ci`
              : `Voici votre situation financière de ${fmtPeriodLabel(periode)}`
          }
        />

        {/* Alerte DSO si seuil dépassé (90 jours OHADA recommandé) */}
        {kpis && kpis.dsoDays > 90 && (
          <div className="ds-alert-strip ds-alert-w">
            <span className="ds-alert-ico"><AlertTriangle size={20} /></span>
            <div className="ds-alert-txt">
              <strong>DSO élevé — {kpis.dsoDays} jours de délai de paiement clients</strong>
              Objectif OHADA : 90 jours. Actions à prendre : relancer les factures en retard.
            </div>
            <div className="ds-alert-cta">
              <a href="/finance/invoices" className="ds-btn ds-btn-g ds-btn-sm">Voir factures →</a>
            </div>
          </div>
        )}

        {/* Consolidé groupe : un chiffre de groupe présenté comme celui d'une
            société serait trompeur. L'écran doit nommer ce qu'il additionne. */}
        {kpis?.consolide && (
          <div className="ds-alert-strip ds-alert-i" style={{ marginBottom: 12 }}>
            <span className="ds-alert-ico"><Earth size={20} /></span>
            <div className="ds-alert-txt">
              <strong>Consolidé groupe — {fmtPeriodLabel(periode)}</strong>
              {(kpis.filialesConsolidees?.length ?? 0) > 0 ? (
                <>
                  {' '}Somme de {kpis.filialesConsolidees!.length} filiale
                  {kpis.filialesConsolidees!.length > 1 ? 's' : ''}&nbsp;:{' '}
                  {kpis.filialesConsolidees!.map((f) => f.name).join(' · ')}.
                  {' '}SIM GROUP ne tient pas de comptabilité d'exploitation&nbsp;: ses
                  propres livres ne sont pas comptés ici.
                </>
              ) : (
                <>
                  {' '}Aucune filiale n'a de chiffres sur cette période. Les montants
                  ci-dessous sont à zéro pour cette raison — le calcul n'est pas en cause.
                </>
              )}
            </div>
            <div className="ds-alert-cta">
              <a href="/holding" className="ds-btn ds-btn-g ds-btn-sm">Détail par filiale →</a>
            </div>
          </div>
        )}

        {/* Periode sans ecriture : un zero sans explication se lit comme une panne.
            Jamais pour la holding : l'inviter à « saisir une écriture » reviendrait
            à lui demander de tenir des livres qu'elle n'a pas vocation à tenir. */}
        {periodeSansEcriture && !kpis?.consolide && (
          <div className="ds-alert-strip ds-alert-i">
            <span className="ds-alert-ico"><Info size={20} /></span>
            <div className="ds-alert-txt">
              <strong>Aucune écriture comptable en {fmtPeriodLabel(periode)}</strong>
              {dernierePeriodeActive ? (
                <>
                  Les montants ci-dessous sont à zéro pour cette raison — le calcul n'est pas en cause.
                  {' '}Dernière activité enregistrée&nbsp;: {fmtPeriodLabel(dernierePeriodeActive.periode)},
                  {' '}{formatMontant(dernierePeriodeActive.caCents)} FCFA de produits.
                </>
              ) : (
                <>
                  Les montants ci-dessous sont à zéro pour cette raison — le calcul n'est pas en cause.
                  {' '}Saisissez vos écritures pour alimenter le tableau de bord.
                </>
              )}
            </div>
            <div className="ds-alert-cta">
              {dernierePeriodeActive ? (
                <button
                  type="button"
                  onClick={() => setPeriode(dernierePeriodeActive.periode)}
                  className="ds-btn ds-btn-g ds-btn-sm"
                >
                  Voir {fmtPeriodLabel(dernierePeriodeActive.periode)} →
                </button>
              ) : (
                <a href="/finance/journal" className="ds-btn ds-btn-g ds-btn-sm">Saisir une écriture →</a>
              )}
            </div>
          </div>
        )}

        {/* KPI Grid 5 cards */}
        <div className="ds-kpi-grid">
          <KpiCard
            label="Chiffre d'affaires"
            value={kpis ? formatMontant(kpis.caCents) : '—'}
            unit="FCFA"
            icon={<Banknote size={19} strokeWidth={2} />}
            sub={`Cumul ${anneeAffichee} : ${formatMontant(cumulAnnuel)} FCFA`}
            trend={computeTrend((s) => s?.caCents)}
          />
          <KpiCard
            label="Marge brute"
            value={kpis ? formatMontant(kpis.margeBruteCents) : '—'}
            unit="FCFA"
            icon={<TrendingUp size={19} strokeWidth={2} />}
            trend={tauxMargeBrute > 0
              ? { label: `Taux ${(tauxMargeBrute * 100).toFixed(0)}%`, direction: 'up' }
              : undefined}
          />
          <KpiCard
            label="EBITDA"
            value={kpis ? formatMontant(kpis.ebitdaCents) : '—'}
            unit="FCFA"
            icon={<Activity size={19} strokeWidth={2} />}
            iconBg="#ecfdf5"
            type="ok"
            trend={tauxEbitda > 0
              ? { label: `Marge ${(tauxEbitda * 100).toFixed(0)}%`, direction: 'up' }
              : undefined}
          />
          <KpiCard
            label="Trésorerie nette"
            value={kpis ? formatMontant(kpis.tresorerieCents) : '—'}
            unit="FCFA"
            icon={<Landmark size={19} strokeWidth={2} />}
            trend={{ label: 'Stable', direction: 'flat' }}
          />
          <KpiCard
            label="BFR"
            value={kpis ? formatMontant(kpis.bfrCents) : '—'}
            unit="FCFA"
            icon={<Scale size={19} strokeWidth={2} />}
            iconBg="#fef3c7"
            type="warn"
            trend={kpis && Number(BigInt(kpis.bfrCents)) > Number(BigInt(kpis.tresorerieCents))
              ? { label: 'À surveiller', direction: 'down' }
              : undefined}
          />
        </div>

        {/* Évolution CA + Répartition charges */}
        <div className="ds-g2">
          <div className="ds-panel">
            <div className="ds-panel-h">
              <div>
                <div className="ds-panel-title">Évolution du chiffre d'affaires</div>
                <div className="ds-panel-sub">6 derniers mois · en milliers FCFA</div>
              </div>
              <span className="ds-bd ds-bd-s">↑ Tendance</span>
            </div>
            {history ? (
              <RevenueChart series={history.series.slice(-6)} loading={historyLoading} />
            ) : (
              <RevenueChart series={[]} loading />
            )}
          </div>

          <div className="ds-panel">
            <div className="ds-panel-h">
              <div>
                <div className="ds-panel-title">Répartition des charges</div>
                <div className="ds-panel-sub">{fmtPeriodLabel(periode)}</div>
              </div>
            </div>
            {chargesTotalCents > 0 ? (
              <ChargesBreakdown
                charges={charges}
                ratios={[
                  { label: 'Taux de marge brute',  pct: Math.round(tauxMargeBrute * 100), color: 'primary' },
                  { label: 'Taux EBITDA',          pct: Math.round(tauxEbitda * 100),     color: 'success' },
                  { label: 'Taux de charge global', pct: Math.round(tauxCharges * 100),   color: 'warning' },
                ]}
              />
            ) : (
              <div className="ds-panel-body" style={{ textAlign: 'center', color: 'var(--tx-3)', fontSize: 13, padding: 40 }}>
                Pas de charges enregistrées sur la période.<br />
                <span style={{ fontSize: 11, opacity: 0.7 }}>Saisir des écritures classes 6xx pour voir la répartition</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer info */}
        {kpis && (
          <div style={{
            borderRadius: 'var(--r-sm)', border: '1px dashed var(--bdr)',
            background: 'var(--surface)', padding: 12, textAlign: 'center',
            fontSize: 11, color: 'var(--tx-3)',
          }}>
            Snapshot calculé le {kpis.computedAt ? new Date(kpis.computedAt).toLocaleString('fr-FR') : 'à la volée'}
            {' · '}Devise locale : <span className="font-m">{kpis.currency}</span>
            {' · '}Recalcul auto toutes les 15 min via scheduler ou après chaque mutation
          </div>
        )}
      </div>
    </>
  );
};

// Exporté pour l'icône Bell utilisée plus haut (notif badge inline)
export const NotifBadge = () => (
  <span style={{
    position: 'absolute', top: 4, right: 4,
    width: 14, height: 14, background: 'var(--danger)',
    borderRadius: '50%', fontSize: 8, fontWeight: 700, color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>3</span>
);
